import {Injectable} from '@angular/core';
import {catchError, Observable, of} from 'rxjs';
import {CommunicationService} from '../http/communication.service';
import {
  SitesFiltersResponse,
  InventoryQualityTablePayload,
  InventoryQualityTableResponse,
  SitePerformanceResponse,
  ContentCategoryResponse,
  CompetitionFiltersResponse,
  CreativeGalleryPayload,
  CreativeGalleryResponse,
  CreativeGalleryDetailed,
  BrandOverviewResponse,
  PerformanceMapResponse,
  CreativeCardPayload,
  SitePerformancePayload,
  PerformanceMapPayload,
  OverviewPayload,
  ContentCategoryPayload,
  PerformanceOverTimePayload,
  MyBrandPerformanceOverTimeResponse,
} from '../components/my-brand-screen/types';
import {
  ContentCategoryDistributionPayload,
  MyCompetitionOverviewResponse,
  MyCompetitionPerformanceByDeviceWidgetPayload,
  MyCompetitionPerformanceByDeviceWidgetResponse,
  MyCompetitionPerformanceByTimeWidgetPayload,
  MyCompetitionPerformanceByTimeWidgetResponse,
  MyCompetitionPerformanceMapWidgetPayload,
  MyCompetitionPerformanceWidgetPayload,
  MyCompetitionPerformanceWidgetResponse,
  MyCompetitionShareOfVoiceWidgetPayload,
  MyCompetitionWidgetPayload,
  MyCompetitionWidgetResponse,
  MyCompetitionWordCloudResponse, SpendEfficiencyOverviewResponse, SpendEfficiencyResponse
} from '../components/my-competition-screen/types';
import {ChartData, WidgetValueField} from '../shared/types/common';
import { DateRangeValue, formatDateRangeForFilters } from '../utils/time';
import {ContentCategoryDistributionResponse} from '../components/my-competition-screen/content-category-distribution/types/types';
import { MyBrandWidgetApiPaths, MyCompetitionWidgetApiPaths } from './types';
import {ListOfSignalsResponse} from '../components/signals-screen/types';

@Injectable({
  providedIn: 'root'
})
export class BrandsService {

  constructor(private communicationService: CommunicationService) {
  }

  getMyBrandInventoryQuality(payload: InventoryQualityTablePayload): Observable<InventoryQualityTableResponse> {
    return this.communicationService.httpPost<InventoryQualityTableResponse>(MyBrandWidgetApiPaths.INVENTORY_QUALITY, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching inventory quality', error);
          throw error;
        }
      ))
  }

  getSitesPerformance(payload: SitePerformancePayload): Observable<SitePerformanceResponse> {
    return this.communicationService.httpPost<SitePerformanceResponse>(MyBrandWidgetApiPaths.SITES_PERFORMANCE, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching site performance', error);
          throw error;
        }
      ))
  }

  getPerformanceMapWorldwide(payload: PerformanceMapPayload): Observable<PerformanceMapResponse> {
    return this.communicationService.httpPost<PerformanceMapResponse>(MyBrandWidgetApiPaths.PERFORMANCE_MAP_WORLDWIDE, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching performance map worldwide', error);
          throw error;
        }
      ))
  }

  getPerformanceMapUsa(payload: PerformanceMapPayload): Observable<PerformanceMapResponse> {
    return this.communicationService.httpPost<PerformanceMapResponse>(MyBrandWidgetApiPaths.PERFORMANCE_MAP_USA, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching performance map usa', error);
          throw error;
        }
      ))
  }

  getCompetitorsPerformanceMapWorldwide(payload:MyCompetitionPerformanceMapWidgetPayload): Observable<PerformanceMapResponse> {
    return this.communicationService.httpPost<PerformanceMapResponse>(MyCompetitionWidgetApiPaths.PERFORMANCE_MAP_WORLDWIDE, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching Competitors performance map worldwide', error);
          throw error;
        }
      ))
  }

  getCompetitorsPerformanceMapUsa(payload:MyCompetitionPerformanceMapWidgetPayload): Observable<PerformanceMapResponse> {
    return this.communicationService.httpPost<PerformanceMapResponse>(MyCompetitionWidgetApiPaths.PERFORMANCE_MAP_USA, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching Competitors performance map usa', error);
          throw error;
        }
      ))
  }

  getContentCategory(payload: ContentCategoryPayload): Observable<ContentCategoryResponse> {
    return this.communicationService.httpPost<ContentCategoryResponse>(MyBrandWidgetApiPaths.CONTENT_CATEGORY_BREAKDOWN, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching content category', error);
          throw error;
        }
      ))
  }

  getMyCompetitionInventoryQuality(payload: InventoryQualityTablePayload): Observable<InventoryQualityTableResponse> {
    return this.communicationService.httpPost<InventoryQualityTableResponse>(MyCompetitionWidgetApiPaths.INVENTORY_QUALITY, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching inventory quality', error);
          throw error;
        }
      ))
  }

  getMyCompetitionFilters(dateRange: DateRangeValue, accountKey?: string): Observable<CompetitionFiltersResponse> {
    return this.communicationService.httpGet<CompetitionFiltersResponse>(MyCompetitionWidgetApiPaths.FILTERS, {params: {...formatDateRangeForFilters(dateRange),accountKey}}).pipe(
      catchError((error) => {
          console.error('Error fetching my competition filters', error);
          throw error;
        }
      ))
  };

  getShareOfVoice(payload: MyCompetitionShareOfVoiceWidgetPayload): Observable<MyCompetitionWidgetResponse> {
    return this.communicationService.httpPost<MyCompetitionWidgetResponse>(MyCompetitionWidgetApiPaths.BRANDS_IMPRESSIONS_DISTRIBUTION, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching share of voice', error);
          throw error;
        }
      ));
  }

  getPerformanceData(payload: MyCompetitionPerformanceWidgetPayload): Observable<MyCompetitionPerformanceWidgetResponse> {
    return this.communicationService.httpPost<MyCompetitionPerformanceWidgetResponse>(MyCompetitionWidgetApiPaths.PERFORMANCE_OVER_TIME, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching performance over time', error);
          throw error;
        }
      ));
  }


  getComparison(payload: MyCompetitionWidgetPayload): Observable<MyCompetitionWidgetResponse> {
    return this.communicationService.httpPost<MyCompetitionWidgetResponse>(MyCompetitionWidgetApiPaths.PERFORMANCE_COMPARISON, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching attention comparison', error);
          throw error;
        }
      ));
  }

  //TODO remove pipe when real data is available
  getMyCreativeGallery(payload: CreativeGalleryPayload): Observable<CreativeGalleryResponse> {
    return this.communicationService.httpPost<CreativeGalleryResponse>(
      MyBrandWidgetApiPaths.CREATIVE_GALLERY,
      {...payload, ...formatDateRangeForFilters(payload)}
    ).pipe(
      catchError(() =>
        of({
          creativesData: [],
          total: 0
        } as CreativeGalleryResponse)
      )
    );
  }

  //TODO remove pipe when real data is available
  getCompetitorsCreativeGallery(payload: CreativeGalleryPayload): Observable<CreativeGalleryResponse> {
    return this.communicationService.httpPost<CreativeGalleryResponse>(MyCompetitionWidgetApiPaths.CREATIVE_GALLERY, {...payload, ...formatDateRangeForFilters(payload)})
      .pipe(
        catchError(() =>
          of({
            creativesData: [],
            total: 0
          } as CreativeGalleryResponse)
        )
      );
  }

  getMyCreativeCard(payload: CreativeCardPayload): Observable<CreativeGalleryDetailed> {
    return this.communicationService.httpPost<CreativeGalleryDetailed>(MyBrandWidgetApiPaths.CREATIVE_DATA, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching attention comparison', error);
          throw error;
        }
      ));
  }

  getCompetitorCreativeCard(payload: CreativeCardPayload): Observable<CreativeGalleryDetailed> {
    return this.communicationService.httpPost<CreativeGalleryDetailed>(MyCompetitionWidgetApiPaths.CREATIVE_DATA, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching attention comparison', error);
          throw error;
        }
      ));
  }

  getMessagingCreativeCard(payload: CreativeCardPayload):Observable<CreativeGalleryDetailed>{
    return this.communicationService.httpPost<CreativeGalleryDetailed>('/dashboard/messaging/creative-data', {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
          console.error('Error fetching creative', error);
          throw error;
        }
      ));
  }

  getBrandOverview(payload: OverviewPayload): Observable<BrandOverviewResponse> {
    return this.communicationService.httpPost<BrandOverviewResponse>(MyBrandWidgetApiPaths.OVERVIEW, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching brand overview', error);
        throw error;
      })
    );
  }

  getPerformanceOverTime(payload: PerformanceOverTimePayload): Observable<MyBrandPerformanceOverTimeResponse> {
    return this.communicationService.httpPost<MyBrandPerformanceOverTimeResponse>(MyBrandWidgetApiPaths.PERFORMANCE_OVER_TIME, { ...payload, ...formatDateRangeForFilters(payload) }).pipe(
      catchError((error) => {
        console.error('Error fetching performance over time', error);
        throw error;
      })
    );
  }

  getMyCompetitionWordCloud(payload: MyCompetitionWidgetPayload): Observable<MyCompetitionWordCloudResponse> {
    return this.communicationService.httpPost<MyCompetitionWordCloudResponse>(MyCompetitionWidgetApiPaths.WORD_CLOUD, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching competition word cloud', error);
        throw error;
      })
    );
  }

  getCompetitorsOverview(payload: MyCompetitionWidgetPayload): Observable<MyCompetitionOverviewResponse> {
    return this.communicationService.httpPost<MyCompetitionOverviewResponse>(MyCompetitionWidgetApiPaths.OVERVIEW, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching competitors overview', error);
        throw error;
      })
    );
  }

  getSpendEfficiencyOverview(payload: MyCompetitionWidgetPayload): Observable<SpendEfficiencyOverviewResponse> {
    return this.communicationService.httpPost<SpendEfficiencyOverviewResponse>('/dashboard/my-competition/spend-efficiency-overview', {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching competitors overview', error);
        throw error;
      })
    );
  }

  getSpendEfficiency(payload: MyCompetitionWidgetPayload): Observable<SpendEfficiencyResponse> {
    return this.communicationService.httpPost<SpendEfficiencyResponse>('/dashboard/my-competition/spend-efficiency', {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching competitors overview', error);
        throw error;
      })
    );
  }

  getContentCategoryDistribution(payload: ContentCategoryDistributionPayload): Observable<ContentCategoryDistributionResponse>{
    return this.communicationService.httpPost<ContentCategoryDistributionResponse>(MyCompetitionWidgetApiPaths.CONTENT_CATEGORY_DISTRIBUTION, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching content category distribution', error);
        throw error;
      })
    );
  }

  getPerformanceByTime(payload: MyCompetitionPerformanceByTimeWidgetPayload): Observable<MyCompetitionPerformanceByTimeWidgetResponse> {
    return this.communicationService.httpPost<MyCompetitionPerformanceByTimeWidgetResponse>(MyCompetitionWidgetApiPaths.PERFORMANCE_BY_TIME, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching performance by time', error);
        throw error;
      })
    );
  }

  getPerformanceByDevice(payload: MyCompetitionPerformanceByDeviceWidgetPayload): Observable<MyCompetitionPerformanceByDeviceWidgetResponse> {
    return this.communicationService.httpPost<MyCompetitionPerformanceByDeviceWidgetResponse>(MyCompetitionWidgetApiPaths.PERFORMANCE_BY_DEVICE, {...payload, ...formatDateRangeForFilters(payload)}).pipe(
      catchError((error) => {
        console.error('Error fetching performance by device', error);
        throw error;
      })
    );
  }

  getListOfSignalsByBrands(payload:any): Observable<ListOfSignalsResponse> {
    return this.communicationService.httpPost<ListOfSignalsResponse>('/polaris/signals/list-by-brands', payload).pipe(
      catchError((error) => {
        console.error('Error fetching performance by time', error);
        throw error;
      })
    );
  }

  sortChartData(data: ChartData[], sortField: WidgetValueField = 'score'): void {
    data.sort((a, b) => ((a[sortField] || 0) > (b[sortField] || 0) ? -1 : (a[sortField] || 0) < (b[sortField] || 0) ? 1 : 0));
  }
}
